/**
 * NPC Stage Contracts
 *
 * Stage-minimal system prompts (800-1500 chars max) that replace verbose base prompts.
 * Focus on output format, required keys, and critical constraints only.
 *
 * © 2025 Sixsmith Games. All rights reserved.
 * This software and associated documentation files are proprietary and confidential.
 */

import { getNpcPromptContractKey, type NpcPromptContractKey } from '../../../src/shared/generation/workflowStageCatalog';

/**
 * Basic Info stage contract (stage-isolated, concise).
 */
export const BASIC_INFO_CONTRACT = `You are the NPC Creator (Basic Info slice).

Return JSON with ONLY these keys (no extras):
- name: string
- title: string (optional honorifics)
- description: string (1-2 sentences)
- appearance: string (1-2 sentences)
- background: string (1-3 sentences)
- species: string
- alignment: string
- class_levels: array of { class: string; level: number }
- location: string
- affiliation: string

If inferred_species is provided in the stage inputs, set species to that exact value.
 (optional)

Rules:
- If an optional field is unknown, omit it (do not add empty strings).
- Forbidden keys: ability_scores, hit_points, armor_class, speed, senses, personality, equipment, feats, class_features, spells, legendary_actions.`;

/**
 * Core Details stage contract — same shape on retries.
 */
export const CORE_DETAILS_CONTRACT = `You are the NPC Creator (Core Details slice).

Return JSON with ONLY these keys (no extras):
- personality_traits: string[]
- ideals: string[]
- bonds: string[]
- flaws: string[]
- goals: string[]
- fears: string[]
- quirks: string[]
- voice_mannerisms: string[]
- hooks: string[]

Rules:
- Minimum 3 items per array; no empty strings; no placeholders.
- Do NOT return a nested "personality" object.
- On retry, return the SAME JSON shape and replace missing/empty arrays with 3–6 concrete items each.`;

/**
 * Stats stage contract — numeric speeds, lowercase abilities.
 */
export const STATS_CONTRACT = `You are the NPC Creator (Stats slice).

Return JSON with ONLY these keys:
- size: string
- ability_scores: { str: number, dex: number, con: number, int: number, wis: number, cha: number }
- proficiency_bonus: number
- speed: { walk: number, fly?: number, swim?: number, climb?: number, burrow?: number }
- armor_class: { value: number, breakdown: string } | number
- hit_points: { average: number, formula: string } | number
- hit_dice: string
- senses: string[]

Rules:
- Ability score keys must be lowercase.
- Speed values are numbers (feet). Example: walk: 30.
- Include a concrete size such as Small, Medium, or Large.
- Include hit_dice as the dice expression only, such as 13d8 or 15d10.
- Return only these keys; do NOT include personality, equipment, or spells.`;

/**
 * Character Build stage contract — isolated keys.
 */
export const CHARACTER_BUILD_CONTRACT = `You are the NPC Creator (Character Build slice).

Return JSON with ONLY these keys:
- class_features: { name: string, level: number, description: string, source: string }[]
- subclass_features: { name: string, level: number, description: string, source: string }[]
- racial_features: { name: string, description: string, source?: string }[]
- feats: { name: string, description: string, source?: string }[]
- fighting_styles: { name: string, description: string, source?: string }[]
- skill_proficiencies: { name: string, value: string }[]
- saving_throws: { name: string, value: string }[]

Rules:
- Every item in class_features, subclass_features, racial_features, feats, and fighting_styles must include a real description explaining what it does.
- Description text must not merely repeat the feature name.
- Keep descriptions concise (1–2 short sentences per feature, focused on concrete mechanics, benefits, triggers, or limitations).
- Skill/save values must be signed strings (e.g., "+7").
- Return only these keys; no stats, personality, or equipment.`;

/**
 * Combat stage contract.
 * Target: ~700 chars
 */
export const COMBAT_CONTRACT = `You are the NPC Creator (Combat slice).

Return JSON with ONLY these keys:
- actions: { name: string, description: string, attack_bonus?: string | number, damage?: string, range?: string, notes?: string }[]
- bonus_actions: { name: string, description: string, uses?: string, notes?: string }[]
- reactions: { name: string, description: string, trigger?: string, notes?: string }[]
- multiattack?: { description: string, attacks_per_action?: number }
- special_attacks?: { name: string, description: string, save_dc?: number, damage?: string, notes?: string }[]

Rules:
- Keep values concise and mechanical; return only these keys.`;

export const EQUIPMENT_CONTRACT = `You are the NPC Creator (Equipment slice).

Return JSON with ONLY these keys:
- weapons: { name: string, notes?: string }[]
- armor_and_shields: { name: string, notes?: string }[]
- wondrous_items: { name: string, notes?: string }[]
- consumables: { name: string, quantity: number, notes?: string }[]
- other_gear: { name: string, notes?: string }[]

Rules:
- Use the user request as the source of truth for named items.
- Return only these keys; no stats or personality fields.`;

/**
 * Spellcasting stage contract — isolated keys.
 */
export const SPELLCASTING_CONTRACT = `You are the NPC Creator (Spellcasting slice).

Return JSON with ONLY these keys (if the class can cast spells):
- spellcasting_ability: string
- spell_save_dc: number
- spell_attack_bonus: number
- spell_slots: { 1?: number, 2?: number, 3?: number, 4?: number, 5?: number, 6?: number, 7?: number, 8?: number, 9?: number }
- prepared_spells: { [level: string]: string[] } (prepared/slots casters)
- always_prepared_spells: { [source: string]: string[] } (granted/domain/oath spells)
- innate_spells: { [usage: string]: string[] } (at will / per day groupings)
- spells_known: string[] (known casters)
- spellcasting_focus: string

Rules:
- Keep values minimal; no descriptions, no extra keys, no nested objects beyond the maps above.
- If the NPC is a slot-based caster, include spell_slots with at least one slot.
- spell_slots must be an object map such as { "5": 3 }, not a sentence.
- Include at least one populated spell list: prepared_spells, always_prepared_spells, innate_spells, or spells_known.
- prepared_spells, always_prepared_spells, and innate_spells must be object maps whose values are arrays of spell names; do not return bare arrays.
- Known casters such as warlocks must include spells_known.
- Prepared casters must include prepared_spells or always_prepared_spells.
- If a field is not applicable, omit it.
- Do NOT include equipment, stats, personality, or narrative text.`;

/**
 * Legendary stage contract (concise).
 */
export const LEGENDARY_CONTRACT = `You are the NPC Creator (Legendary slice).

If the request says there are NO legendary actions, return an empty legendary payload.

Return JSON with ONLY these keys (if legendary creature):
- legendary_actions: { summary: string, options: { name: string, description: string, cost?: number }[] }
- legendary_resistance: { uses_per_day: number, description: string }

Optional:
- lair_actions: { name: string, description: string, initiative_count?: number }[]
- regional_effects: { description: string, range?: string }[]

Rules:
- If not legendary, set legendary_actions to empty arrays and omit legendary_resistance.`;

/**
 * Relationships stage contract (concise, isolated).
 */
export const RELATIONSHIPS_CONTRACT = `You are the NPC Creator (Relationships slice).

Return JSON with ONLY these keys:
- allies: { name: string, relationship: string, notes?: string }[]
- enemies: { name: string, relationship: string, notes?: string }[]
- organizations: { name: string, role: string, standing?: string, notes?: string }[]
Optional keys:
- family: { name: string, relationship: string, status?: string }[]
- contacts: { name: string, profession: string, location?: string, notes?: string }[]

Rules:
- Provide 2–4 concrete relationships tied to the character’s background.
- Return only these keys; do NOT include personality or stats.`;

/**
 * Stage contract map.
 * Maps stage IDs to their contracts.
 */
export const STAGE_CONTRACTS: Record<NpcPromptContractKey, string> = {
  basic_info: BASIC_INFO_CONTRACT,
  core_details: CORE_DETAILS_CONTRACT,
  stats: STATS_CONTRACT,
  character_build: CHARACTER_BUILD_CONTRACT,
  combat: COMBAT_CONTRACT,
  equipment: EQUIPMENT_CONTRACT,
  spellcasting: SPELLCASTING_CONTRACT,
  legendary: LEGENDARY_CONTRACT,
  relationships: RELATIONSHIPS_CONTRACT,
};

/**
 * Gets the contract for a stage by ID or name.
 *
 * @param stageIdOrName - Stage ID or name
 * @returns Stage contract or null if not found
 */
export function getStageContract(stageIdOrName: string): string | null {
  const contractKey = getNpcPromptContractKey(stageIdOrName);
  return contractKey ? STAGE_CONTRACTS[contractKey] : null;
}
